import React, { useRef, useState, useEffect } from "react";
import type {
  MessageResponse,
  EvaluationResponse,
  CommentResponse,
  RuleResponse,
  KnowledgeItemResponse,
  GuardrailItemResponse,
  BatchResponse,
} from "../../gen/models";
import {
  type AgentInfo,
  createGovernanceLookups,
} from "./threadUtils";
import { ThreadMessageTurn } from "./ThreadMessageTurn";
import { ThreadCommentCard } from "./ThreadCommentCard";
import { ThreadGeneralNotes } from "./ThreadGeneralNotes";
import { AgentWaitingIndicator } from "./AgentWaitingIndicator";
import { ScrollToBottomButton } from "./ScrollToBottomButton";
import { InPlaceCommentComposer, type InPlaceComposerState } from "./InPlaceCommentComposer";

interface ThreadMessagesStreamProps {
  messages: MessageResponse[];
  evaluations: EvaluationResponse[];
  comments: CommentResponse[];
  rulesList: RuleResponse[];
  guardrailsList?: GuardrailItemResponse[];
  knowledgeList: KnowledgeItemResponse[];
  activeBatches: BatchResponse[];
  allBatches?: BatchResponse[];
  agentInfo: AgentInfo;
  activeHighlightedCommentId: string | null;
  editingCommentId: string | null;
  editingText: string;
  editingRuleIds: string[];
  editingKnowledgeIds: string[];
  savingEdit: boolean;
  mentionType: "rule" | "knowledge" | null;
  mentionQuery: string;
  isWaitingForAgent: boolean;
  inPlaceComposer: InPlaceComposerState | null;
  composerText: string;
  composerRuleIds: string[];
  composerKnowledgeIds: string[];
  savingComposer: boolean;
  composerMentionType: "rule" | "knowledge" | null;
  composerMentionQuery: string;
  activeBatch?: BatchResponse;
  onCloseComposer: () => void;
  onComposerTextChange: (val: string) => void;
  onSaveComposerComment: () => void;
  onOpenBatchModal?: () => void;
  onComposerSelectMentionRule: (rule: RuleResponse) => void;
  onComposerSelectMentionKnowledge: (k: KnowledgeItemResponse) => void;
  onComposerRemoveRule: (rid: string) => void;
  onComposerRemoveKnowledge: (kid: string) => void;
  onSelectText: (messageId: string) => void;
  onOpenTurnComposer: (messageId: string, evalId?: string) => void;
  onOpenEvalDrawer: (evalItem: EvaluationResponse, msgId: string, runId: string | null) => void;
  onSimulateTurn: (msgId: string, baselineContent: string, prevUserPrompt?: string) => void;
  onSetActiveHighlightedCommentId: (id: string | null) => void;
  onStartEditComment: (comment: CommentResponse) => void;
  onCancelEditComment: () => void;
  onSaveEditComment: (commentId: string) => void;
  onDeleteComment: (commentId: string) => void;
  onEditTextChange: (val: string) => void;
  onSelectMentionRule: (rule: RuleResponse) => void;
  onSelectMentionKnowledge: (k: KnowledgeItemResponse) => void;
  onRemoveEditingRule: (ruleId: string) => void;
  onRemoveEditingKnowledge: (knowledgeId: string) => void;
  onDismissWaiting: () => void;
  onInspectRule?: (ruleId: string, rule?: any) => void;
  onInspectGuardrail?: (guardrailId: string, guardrail?: any) => void;
  onInspectKnowledge?: (knowledgeId: string, knowledge?: any) => void;
  onInspectChunk?: (chunkId: string) => void;
  runsMap?: Map<string, any>;
  onAddAlteredNote?: (messageId: string, originalMessage: string, actualMessage: string, evalId?: string) => Promise<void> | void;
}

export function ThreadMessagesStream({
  messages,
  evaluations,
  comments,
  rulesList,
  guardrailsList = [],
  knowledgeList,
  activeBatches,
  allBatches = [],
  agentInfo,
  activeHighlightedCommentId,
  editingCommentId,
  editingText,
  editingRuleIds,
  editingKnowledgeIds,
  savingEdit,
  mentionType,
  mentionQuery,
  isWaitingForAgent,
  inPlaceComposer,
  composerText,
  composerRuleIds,
  composerKnowledgeIds,
  savingComposer,
  composerMentionType,
  composerMentionQuery,
  activeBatch,
  onCloseComposer,
  onComposerTextChange,
  onSaveComposerComment,
  onOpenBatchModal,
  onComposerSelectMentionRule,
  onComposerSelectMentionKnowledge,
  onComposerRemoveRule,
  onComposerRemoveKnowledge,
  onSelectText,
  onOpenTurnComposer,
  onOpenEvalDrawer,
  onSimulateTurn,
  onSetActiveHighlightedCommentId,
  onStartEditComment,
  onCancelEditComment,
  onSaveEditComment,
  onDeleteComment,
  onEditTextChange,
  onSelectMentionRule,
  onSelectMentionKnowledge,
  onRemoveEditingRule,
  onRemoveEditingKnowledge,
  onDismissWaiting,
  onInspectRule,
  onInspectGuardrail,
  onInspectKnowledge,
  onInspectChunk,
  runsMap,
  onAddAlteredNote,
}: ThreadMessagesStreamProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showScrollBottomArrow, setShowScrollBottomArrow] = useState(false);
  const [expandedUserMsgIds, setExpandedUserMsgIds] = useState<Record<string, boolean>>({});

  const displayBatches = allBatches.length > 0 ? allBatches : activeBatches;

  const { deduplicateRules, deduplicateAndGroupSources, deduplicateGuardrails } = React.useMemo(
    () => createGovernanceLookups(rulesList, knowledgeList, guardrailsList),
    [rulesList, knowledgeList, guardrailsList]
  );

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const isNotAtBottom = scrollHeight - scrollTop - clientHeight > 60;
    setShowScrollBottomArrow(isNotAtBottom);
  };

  const scrollToBottom = (smooth = true) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => scrollToBottom(false), 50);
    }
  }, [messages.length]);

  const toggleUserMsgExpand = (id: string) => {
    setExpandedUserMsgIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderCommentCard = (c: CommentResponse) => (
    <ThreadCommentCard
      key={c.id}
      comment={c}
      rulesList={rulesList}
      knowledgeList={knowledgeList}
      batch={displayBatches.find((b) => b.id === c.batch_id)}
      isHighlighted={c.id === activeHighlightedCommentId}
      isEditing={c.id === editingCommentId}
      editingText={editingText}
      editingRuleIds={editingRuleIds}
      editingKnowledgeIds={editingKnowledgeIds}
      savingEdit={savingEdit}
      mentionType={mentionType}
      mentionQuery={mentionQuery}
      mentionTarget="editor"
      onCardClick={(commentItem) => {
        onSetActiveHighlightedCommentId(commentItem.id);
        if (commentItem.message_id) {
          const msgElem = document.getElementById(`msg-${commentItem.message_id}`);
          msgElem?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }}
      onStartEdit={onStartEditComment}
      onCancelEdit={onCancelEditComment}
      onSaveEdit={onSaveEditComment}
      onDeleteComment={onDeleteComment}
      onEditTextChange={onEditTextChange}
      onSelectMentionRule={onSelectMentionRule}
      onSelectMentionKnowledge={onSelectMentionKnowledge}
      onRemoveEditingRule={onRemoveEditingRule}
      onRemoveEditingKnowledge={onRemoveEditingKnowledge}
      onInspectRule={onInspectRule}
      onInspectKnowledge={onInspectKnowledge}
    />
  );

  const renderComposerCard = () => {
    if (!inPlaceComposer) return null;
    return (
      <InPlaceCommentComposer
        composer={inPlaceComposer}
        composerText={composerText}
        composerRuleIds={composerRuleIds}
        composerKnowledgeIds={composerKnowledgeIds}
        rulesList={rulesList}
        knowledgeList={knowledgeList}
        activeBatch={activeBatch}
        savingComposer={savingComposer}
        mentionType={composerMentionType}
        mentionQuery={composerMentionQuery}
        mentionTarget="composer"
        onOpenBatchModal={onOpenBatchModal}
        onClose={onCloseComposer}
        onTextChange={onComposerTextChange}
        onSave={onSaveComposerComment}
        onSelectMentionRule={onComposerSelectMentionRule}
        onSelectMentionKnowledge={onComposerSelectMentionKnowledge}
        onRemoveRule={onComposerRemoveRule}
        onRemoveKnowledge={onComposerRemoveKnowledge}
      />
    );
  };

  return (
    <>
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-6 [scrollbar-gutter:stable] relative"
      >
        <div className="w-full max-w-5xl mx-auto space-y-6">
          {messages.length === 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              <div className="lg:col-span-8 p-16 text-center text-xs text-stone-500">
                No turn messages recorded yet. Send your first message below.
              </div>
            </div>
          ) : (
            messages.map((msg, mIdx) => {
              const isAgent = msg.sender_role === "agent" || msg.sender_role === "assistant";
              const metadata = (msg.metadata || {}) as Record<string, any>;
              const quoteDraft = metadata.quote_draft as Record<string, any> | undefined;

              const isGuardrailBlocked = Boolean(
                metadata.guardrail_blocked ||
                (metadata.status === "blocked" && (metadata.category || metadata.guardrails_applied))
              );

              const rawRulesList: any[] = [];
              if (!isGuardrailBlocked) {
                if (Array.isArray(metadata.rules_applied_details)) rawRulesList.push(...metadata.rules_applied_details);
                if (Array.isArray(quoteDraft?.rules_applied_details)) rawRulesList.push(...quoteDraft.rules_applied_details);
                if (Array.isArray(metadata.draft?.rules_applied_details)) rawRulesList.push(...metadata.draft.rules_applied_details);
                if (Array.isArray(metadata.rules_applied)) rawRulesList.push(...metadata.rules_applied);
              }

              const rawGuardrailsList: any[] = [];
              if (Array.isArray(metadata.guardrails_applied_details)) rawGuardrailsList.push(...metadata.guardrails_applied_details);
              if (Array.isArray(metadata.guardrails_applied)) rawGuardrailsList.push(...metadata.guardrails_applied);
              if (Array.isArray(metadata.guardrails)) rawGuardrailsList.push(...metadata.guardrails);
              if (isGuardrailBlocked && rawGuardrailsList.length === 0) {
                rawGuardrailsList.push({
                  id: metadata.matched_item_id,
                  title: metadata.matched_item_title || (metadata.category ? `Guardrail: ${metadata.category}` : "Guardrail Policy"),
                  category: metadata.category || "safety",
                  similarity_score: metadata.similarity_score,
                  anchor_text: metadata.anchor_text,
                  action: metadata.action || metadata.on_block_action || "block",
                  severity: metadata.severity,
                });
              }

              const rawSourcesList: any[] = [];
              if (Array.isArray(metadata.sources_consulted_details)) rawSourcesList.push(...metadata.sources_consulted_details);
              if (Array.isArray(quoteDraft?.sources_consulted_details)) rawSourcesList.push(...quoteDraft.sources_consulted_details);
              if (Array.isArray(metadata.draft?.sources_consulted_details)) rawSourcesList.push(...metadata.draft.sources_consulted_details);
              if (Array.isArray(metadata.sources_consulted)) rawSourcesList.push(...metadata.sources_consulted);
              if (Array.isArray(quoteDraft?.sources_consulted)) rawSourcesList.push(...quoteDraft.sources_consulted);
              if (Array.isArray(metadata.draft?.sources_consulted)) rawSourcesList.push(...metadata.draft.sources_consulted);

              // Additional explicit tool arrays if present in turn metadata
              if (Array.isArray(metadata.tools_used)) {
                rawSourcesList.push(...metadata.tools_used.map((t: any) => typeof t === "string" ? { isTool: true, title: t } : { ...t, isTool: true }));
              }
              if (Array.isArray(metadata.tools)) {
                rawSourcesList.push(...metadata.tools.map((t: any) => typeof t === "string" ? { isTool: true, title: t } : { ...t, isTool: true }));
              }
              if (Array.isArray(metadata.tool_calls)) {
                rawSourcesList.push(...metadata.tool_calls.map((t: any) => ({ isTool: true, title: t.tool_name || t.tool || t.name || String(t) })));
              }
              if (Array.isArray(metadata.executed_tools)) {
                rawSourcesList.push(...metadata.executed_tools.map((t: any) => typeof t === "string" ? { isTool: true, title: t } : { ...t, isTool: true }));
              }
              if (Array.isArray(metadata.draft?.tools_used)) {
                rawSourcesList.push(...metadata.draft.tools_used.map((t: any) => typeof t === "string" ? { isTool: true, title: t } : { ...t, isTool: true }));
              }
              if (Array.isArray(quoteDraft?.tools_used)) {
                rawSourcesList.push(...quoteDraft.tools_used.map((t: any) => typeof t === "string" ? { isTool: true, title: t } : { ...t, isTool: true }));
              }
              if (metadata.requires_live_rates || quoteDraft?.requires_live_rates || metadata.draft?.requires_live_rates) {
                rawSourcesList.push({ isTool: true, title: "Mirai Live Rates Engine", category: "live_rates" });
              }

              const resolvedRules = isAgent ? deduplicateRules(rawRulesList) : [];
              const resolvedGuardrails = isAgent ? deduplicateGuardrails(rawGuardrailsList) : [];
              const sourcesGroup = isAgent
                ? deduplicateAndGroupSources(rawSourcesList)
                : { groups: [], totalChunks: 0, knowledgeItemCount: 0, toolCount: 0 };

              const turnEval = evaluations.find(
                (e) => e.message_id === msg.id || (msg.run_id && e.run_id === msg.run_id)
              );

              const turnComments = comments
                .filter((c) => c.message_id === msg.id)
                .sort((a, b) => {
                  const aStart = a.selection_start ?? 0;
                  const bStart = b.selection_start ?? 0;
                  if (aStart !== bStart) return aStart - bStart;
                  const aEnd = a.selection_end ?? 0;
                  const bEnd = b.selection_end ?? 0;
                  if (aEnd !== bEnd) return aEnd - bEnd;
                  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                });

              const prevUserMsg = messages
                .slice(0, mIdx)
                .reverse()
                .find((m) => m.sender_role === "user" || m.sender_role === "patron");

              const isTurnComposerActive = inPlaceComposer?.messageId === msg.id;
              const activeSelection =
                isTurnComposerActive && inPlaceComposer?.selectedText
                  ? {
                      text: inPlaceComposer.selectedText,
                      start: inPlaceComposer.start,
                      end: inPlaceComposer.end,
                    }
                  : null;

              const associatedRun = (msg.run_id && runsMap?.get(msg.run_id)) || (runsMap?.get(msg.id));
              const runTelemetry = associatedRun?.telemetry;

              return (
                <ThreadMessageTurn
                  key={msg.id}
                  message={msg}
                  turnIndex={mIdx}
                  agentInfo={agentInfo}
                  turnComments={turnComments}
                  activeHighlightedCommentId={activeHighlightedCommentId}
                  activeSelection={activeSelection}
                  turnEval={turnEval}
                  resolvedRules={resolvedRules}
                  resolvedGuardrails={resolvedGuardrails}
                  sourcesGroup={sourcesGroup}
                  hasThreadComments={true}
                  isUserExpanded={Boolean(expandedUserMsgIds[msg.id])}
                  isComposerActive={isTurnComposerActive}
                  renderComposerCard={renderComposerCard}
                  onToggleUserExpand={toggleUserMsgExpand}
                  onSelectText={onSelectText}
                  runTelemetry={runTelemetry}
                  onCommentClick={(cid) => {
                    onSetActiveHighlightedCommentId(cid);
                    const card = document.getElementById(`comment-card-${cid}`);
                    card?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                  }}
                  onOpenEvalDrawer={onOpenEvalDrawer}
                  onSimulateTurn={onSimulateTurn}
                  onOpenComposerForTurn={onOpenTurnComposer}
                  renderCommentCard={renderCommentCard}
                  prevUserMessageContent={prevUserMsg?.content}
                  onInspectRule={onInspectRule}
                  onInspectGuardrail={onInspectGuardrail}
                  onInspectKnowledge={onInspectKnowledge}
                  onInspectChunk={onInspectChunk}
                  onAddAlteredNote={onAddAlteredNote}
                />
              );
            })
          )}

          {/* Thread-level General Notes */}
          <ThreadGeneralNotes
            comments={comments}
            rulesList={rulesList}
            knowledgeList={knowledgeList}
            activeBatches={activeBatches}
            allBatches={displayBatches}
            activeHighlightedCommentId={activeHighlightedCommentId}
            editingCommentId={editingCommentId}
            editingText={editingText}
            editingRuleIds={editingRuleIds}
            editingKnowledgeIds={editingKnowledgeIds}
            savingEdit={savingEdit}
            mentionType={mentionType}
            mentionQuery={mentionQuery}
            isComposerActive={inPlaceComposer !== null && !inPlaceComposer.messageId}
            renderComposerCard={renderComposerCard}
            onCardClick={(commentItem) => onSetActiveHighlightedCommentId(commentItem.id)}
            onStartEdit={onStartEditComment}
            onCancelEdit={onCancelEditComment}
            onSaveEdit={onSaveEditComment}
            onDeleteComment={onDeleteComment}
            onEditTextChange={onEditTextChange}
            onSelectMentionRule={onSelectMentionRule}
            onSelectMentionKnowledge={onSelectMentionKnowledge}
            onRemoveEditingRule={onRemoveEditingRule}
            onRemoveEditingKnowledge={onRemoveEditingKnowledge}
            onInspectRule={onInspectRule}
            onInspectKnowledge={onInspectKnowledge}
          />

          {/* Thinking Indicator */}
          {isWaitingForAgent && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              <div className="lg:col-span-8">
                <AgentWaitingIndicator onDismiss={onDismissWaiting} />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      <ScrollToBottomButton show={showScrollBottomArrow} onClick={() => scrollToBottom(true)} />
    </>
  );
}
